// React hooks for state management and side effects
import React, { useState, useEffect } from 'react';
// React Router hook to programmatically navigate users
import { useNavigate } from 'react-router-dom';
// Custom authentication hook to access user info and loading state
import { useAuth } from '../hooks/useAuth';
// Axios library for making HTTP requests
import axios from 'axios';

function CreateCoursePage() {
  // Destructure user and loading state from authentication hook
  const { user, isLoading } = useAuth();
  // Local state for course name input
  const [name, setName] = useState('');
  // Local state for course description input
  const [description, setDescription] = useState('');
  // Local state for optional file upload
  const [file, setFile] = useState<File | null>(null);
  // Local state to store error messages
  const [error, setError] = useState<string | null>(null);
  // React Router navigation hook
  const navigate = useNavigate();
  // Redirect non-teacher users away from this page
  useEffect(() => {
    if (!isLoading && user?.role !== 'teacher') {
      navigate('/courses');
    }
  }, [isLoading, user, navigate]);
  // Form submission handler
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault(); // Prevent default form submission
    setError(null); // Clear previous errors
    // Validate course name
    if (!name.trim() || name.length > 200) {
      setError('Course name is required and must be 200 characters or less.');
      return;
    }
    // Validate description
    if (!description.trim()) {
      setError('Description is required.');
      return;
    }
    // Validate optional file size
    if (file && file.size > 10 * 1024 * 1024) {
      setError('File size must be less than 10MB.');
      return;
    }
    try {
      // Fetch CSRF token from backend for secure POST request
      const csrfResponse = await axios.get('/elearning/get-csrf/', { withCredentials: true });
      const csrfToken = csrfResponse.data.csrfToken;
      // Prepare FormData for multipart/form-data POST request
      const formData = new FormData();
      formData.append('name', name.trim());
      formData.append('description', description.trim());
      if (file) {
        formData.append('materials', file);
      }
      // Send POST request to create the course
      await axios.post('/elearning/api/courses/', formData, {
        withCredentials: true,
        headers: {
          'Content-Type': 'multipart/form-data',
          'X-CSRFToken': csrfToken, // Include CSRF token in headers
        },
      });
      navigate('/courses'); // Navigate to courses list after successful creation
    } catch (err: any) {
      // Handle API errors and display them
      const errorMessage = err.response?.data
        ? JSON.stringify(err.response.data, null, 2)
        : err.message || 'Failed to create course';
      console.error('CreateCoursePage: Error creating course=', errorMessage);
      setError(errorMessage);
    }
  };
  // Display loading spinner while authentication is loading
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-blue-600"></div>
      </div>
    );
  }
  // Render nothing if user is not logged in
  if (!user) return null;
  // Main page content
return (
  <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 font-sans">
    <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
      {/* Page heading */}
      <h1 className="text-4xl font-extrabold text-gray-900 mb-10 tracking-tight">
        🎓 Create a New Course
      </h1>

      {/* Error message display */}
      {error && (
        <div className="mb-8 p-4 bg-red-100 text-red-800 rounded-xl shadow flex items-start">
          <svg
            className="h-5 w-5 mr-2 mt-0.5 text-red-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <pre className="whitespace-pre-wrap">{error}</pre>
        </div>
      )}

      {/* Course creation form */}
      <form
        onSubmit={handleSubmit}
        className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 max-w-2xl"
      >
        {/* Course name input */}
        <div className="mb-6">
          <label
            htmlFor="name"
            className="block text-sm font-semibold text-gray-700 mb-1"
          >
            Course Name
          </label>
          <input
            type="text"
            id="name"
            value={name}
            onChange={e => setName(e.target.value)}
            className="mt-1 px-3 py-2 w-full border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-blue-600 transition"
            required
            maxLength={200}
          />
        </div>

        {/* Course description input */}
        <div className="mb-6">
          <label
            htmlFor="description"
            className="block text-sm font-semibold text-gray-700 mb-1"
          >
            Description
          </label>
          <textarea
            id="description"
            value={description}
            onChange={e => setDescription(e.target.value)}
            className="mt-1 px-3 py-2 w-full border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-blue-600 transition"
            rows={4}
            required
          />
        </div>

        {/* Optional file upload */}
        <div className="mb-6">
          <label
            htmlFor="materials"
            className="block text-sm font-semibold text-gray-700 mb-1"
          >
            Materials (optional)
          </label>
          <input
            type="file"
            id="materials"
            onChange={e => setFile(e.target.files?.[0] || null)}
            className="mt-1 block w-full text-sm text-gray-700 border rounded-xl cursor-pointer bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-600 transition"
            accept=".pdf,.doc,.docx,.txt,.jpg,.png"
          />
          <p className="mt-1 text-xs text-gray-500">
            Accepted formats: PDF, DOC, DOCX, TXT, JPG, PNG
          </p>
        </div>

        {/* Form action buttons */}
        <div className="flex space-x-4">
          <button
            type="submit"
            className="px-5 py-2.5 bg-blue-600 text-white rounded-xl shadow hover:bg-blue-700 transition"
          >
            🚀 Create Course
          </button>
          <a
            href="/courses"
            className="px-5 py-2.5 bg-gray-200 text-gray-700 rounded-xl shadow hover:bg-gray-300 transition"
          >
            ❌ Cancel
          </a>
        </div>
      </form>
    </div>
  </div>
);

}
// Export the component to be used in the app
export default CreateCoursePage;
